package com.partejar.app.feature.account.presentation

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.partejar.app.feature.account.data.AccountDeletionReceipt
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.theme.PartejarTheme
import org.koin.compose.koinInject

@Composable
fun AccountDeletionScreen(
    onBack: () -> Unit,
    onSuccess: (AccountDeletionReceipt) -> Unit,
    viewModel: AccountDeletionViewModel = koinInject()
) {
    val state by viewModel.state.collectAsState()
    AccountDeletionContent(state, onBack, viewModel::begin, viewModel::cancel) {
        viewModel.confirm(onSuccess)
    }
}

@Composable
private fun AccountDeletionContent(
    state: AccountDeletionState,
    onBack: () -> Unit,
    onBegin: () -> Unit,
    onCancel: () -> Unit,
    onConfirm: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    Scaffold(topBar = { PartejarTopBar(title = "Excluir minha conta") }) { padding ->
        Column(
            modifier = Modifier.fillMaxSize().padding(padding)
                .verticalScroll(rememberScrollState())
                .padding(PaddingValues(spacing.space16)),
            verticalArrangement = Arrangement.spacedBy(spacing.space16)
        ) {
            Text("Solicitar exclusão da conta profissional", style = MaterialTheme.typography.titleLarge)
            Text(
                "Ao confirmar, você perderá o acesso à sua conta profissional. " +
                    "Alguns registros podem ser preservados quando houver obrigação legal ou necessidade legítima de retenção.",
                style = MaterialTheme.typography.bodyLarge
            )
            Text(
                "Dados de pacientes, contratos e espaços de trabalho não serão apagados automaticamente. " +
                    "Se você for proprietária ou a última administradora de um espaço, a situação precisará ser resolvida antes da conclusão.",
                style = MaterialTheme.typography.bodyMedium
            )

            when (state) {
                AccountDeletionState.Ready, is AccountDeletionState.Error -> {
                    if (state is AccountDeletionState.Error) {
                        Text(state.message, color = MaterialTheme.colorScheme.error)
                    }
                    PartejarButton("Quero excluir minha conta", onBegin, variant = PartejarButtonVariant.DESTRUCTIVE)
                    PartejarButton("Voltar", onBack, variant = PartejarButtonVariant.SECONDARY)
                }
                AccountDeletionState.Confirming, AccountDeletionState.Loading -> {
                    Text(
                        "Confirme a solicitação. O acesso será bloqueado e suas sessões serão encerradas.",
                        style = MaterialTheme.typography.bodyLarge
                    )
                    PartejarButton(
                        "Confirmar solicitação de exclusão",
                        onConfirm,
                        isLoading = state == AccountDeletionState.Loading,
                        enabled = state != AccountDeletionState.Loading,
                        variant = PartejarButtonVariant.DESTRUCTIVE
                    )
                    PartejarButton(
                        "Cancelar",
                        onCancel,
                        enabled = state != AccountDeletionState.Loading,
                        variant = PartejarButtonVariant.SECONDARY
                    )
                }
                is AccountDeletionState.Success -> Unit
            }
        }
    }
}

@Composable
fun AccountDeletionConfirmationScreen(
    alreadyRequested: Boolean,
    requiresWorkspaceResolution: Boolean,
    onLogin: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = Modifier.fillMaxSize().widthIn(max = 640.dp).verticalScroll(rememberScrollState())
            .padding(spacing.space24),
        verticalArrangement = Arrangement.spacedBy(spacing.space16)
    ) {
        Text("Partejar", style = MaterialTheme.typography.titleLarge)
        Text(
            if (alreadyRequested) "Sua solicitação já estava registrada."
            else "Sua solicitação de exclusão foi registrada.",
            style = MaterialTheme.typography.headlineSmall
        )
        Text("O acesso à conta profissional foi bloqueado. O pedido ainda não foi concluído.")
        if (requiresWorkspaceResolution) {
            Text("Há espaço(s) que precisam de resolução de propriedade ou administração antes da conclusão.")
        }
        Text("Alguns registros podem ser preservados quando houver obrigação legal ou necessidade legítima de retenção.")
        PartejarButton("Voltar ao login", onLogin, variant = PartejarButtonVariant.SECONDARY)
    }
}

@Composable
fun PublicAccountDeletionScreen(onSignIn: () -> Unit) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = Modifier.fillMaxSize().widthIn(max = 640.dp).verticalScroll(rememberScrollState())
            .padding(spacing.space24),
        verticalArrangement = Arrangement.spacedBy(spacing.space16)
    ) {
        Text("Partejar", style = MaterialTheme.typography.titleLarge)
        Text("Solicitar exclusão da conta", style = MaterialTheme.typography.headlineSmall)
        Text(
            "Esta página é para profissionais com conta no Partejar. Entre na sua conta e confirme a solicitação em duas etapas. " +
                "Após o pedido, o acesso será bloqueado. A exclusão definitiva depende da análise dos vínculos da conta."
        )
        Text("Alguns registros podem ser preservados quando houver obrigação legal ou necessidade legítima de retenção.")
        PartejarButton("Entrar para solicitar exclusão", onSignIn)
    }
}
